import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";

/**
 * Copyright IBM Corp. 2016, 2020
 *
 * This source code is licensed under the Apache-2.0 license found in the
 * LICENSE file in the root directory of this source tree.
 */
import React, { useRef } from 'react';
import ddsSettings from '@carbon/ibmdotcom-utilities/es/utilities/settings/settings';
import HeaderSideNavItems from '../../internal/vendor/carbon-components-react/components/UIShell/HeaderSideNavItems';
import PropTypes from 'prop-types';
import settings from 'carbon-components/es/globals/js/settings';
import SideNav from '../../internal/vendor/carbon-components-react/components/UIShell/SideNav';
import SideNavItems from '../../internal/vendor/carbon-components-react/components/UIShell/SideNavItems';
import SideNavLink from '../../internal/vendor/carbon-components-react/components/UIShell/SideNavLink';
import SideNavMenuItem from '../../internal/vendor/carbon-components-react/components/UIShell/SideNavMenuItem';
import SideNavMenuWithBackFoward from '../carbon-components-react/UIShell/SideNavMenuWithBackForward';
var stablePrefix = ddsSettings.stablePrefix;
var prefix = settings.prefix;
/**
 * Masthead left nav component.
 */

var MastheadLeftNav = function MastheadLeftNav(_ref) {
  var backButtonText = _ref.backButtonText,
      navigation = _ref.navigation,
      isSideNavExpanded = _ref.isSideNavExpanded,
      platform = _ref.platform,
      rest = _objectWithoutProperties(_ref, ["backButtonText", "navigation", "isSideNavExpanded", "platform"]);

  var sideNavRef = useRef();
  /**
   * Left side navigation
   *
   * @returns {*} Left side navigation
   */

  var sideNav = navigation.map(function (link, i) {
    if (link.hasMenu || link.hasMegaPanel) {
      var autoid = "".concat(stablePrefix, "--masthead-").concat(rest.navType, "-sidenav__l0-nav").concat(i);
      var dataTitle = link.titleEnglish ? link.titleEnglish.replace(/[^-a-zA-Z0-9_ ]/g, '').replace(/ +/g, '-').toLowerCase() : null;

      if (navigation.length === i + 1) {
        return React.createElement(React.Fragment, null, React.createElement(SideNavMenuWithBackFoward, {
          title: link.title,
          backButtonText: backButtonText,
          key: i,
          autoid: autoid,
          selected: rest.selectedMenuItem === link.titleEnglish,
          navType: rest.navType,
          dataTitle: dataTitle
        }, renderNavSections(link.menuSections, backButtonText, autoid, rest.navType)), React.createElement("button", {
          className: "".concat(prefix, "--masthead__focus"),
          onFocus: function onFocus() {
            var _sideNavRef$current;

            preventOutFocus((_sideNavRef$current = sideNavRef.current) === null || _sideNavRef$current === void 0 ? void 0 : _sideNavRef$current.parentNode.querySelector(".".concat(prefix, "--header__menu-toggle")), isSideNavExpanded);
          },
          "aria-hidden": true
        }));
      }

      return React.createElement(SideNavMenuWithBackFoward, {
        title: link.title,
        backButtonText: backButtonText,
        key: i,
        autoid: autoid,
        selected: rest.selectedMenuItem === link.titleEnglish,
        navType: rest.navType,
        dataTitle: dataTitle
      }, renderNavSections(link.menuSections, backButtonText, autoid, rest.navType));
    } else {
      if (navigation.length === i + 1) {
        return React.createElement(React.Fragment, null, React.createElement(SideNavLink, {
          href: link.url,
          className: rest.selected && "".concat(prefix, "--masthead__side-nav--submemu--selected"),
          "data-autoid": "".concat(stablePrefix, "--masthead-").concat(rest.navType, "-sidenav__l0-nav").concat(i),
          key: i
        }, link.title), React.createElement("button", {
          className: "".concat(prefix, "--masthead__focus"),
          onFocus: function onFocus() {
            var _sideNavRef$current2;

            preventOutFocus((_sideNavRef$current2 = sideNavRef.current) === null || _sideNavRef$current2 === void 0 ? void 0 : _sideNavRef$current2.parentNode.querySelector(".".concat(prefix, "--header__menu-toggle")), isSideNavExpanded);
          },
          "aria-hidden": true
        }));
      }

      return React.createElement(SideNavLink, {
        href: link.url,
        className: rest.selected && "".concat(prefix, "--masthead__side-nav--submemu--selected"),
        "data-autoid": "".concat(stablePrefix, "--masthead-").concat(rest.navType, "-sidenav__l0-nav").concat(i),
        key: i
      }, link.title);
    }
  });
  return React.createElement(SideNav, {
    "aria-label": "Side navigation",
    expanded: isSideNavExpanded,
    isPersistent: false,
    ref: sideNavRef
  }, React.createElement("nav", {
    "data-autoid": "".concat(stablePrefix, "--masthead-").concat(rest.navType, "-sidenav__l0")
  }, platform && React.createElement("a", {
    // eslint-disable-line jsx-a11y/role-supports-aria-props
    "data-autoid": "".concat(stablePrefix, "--masthead-").concat(rest.navType, "-sidenav__l0-productname"),
    href: platform.url,
    "aria-haspopup": "true",
    className: "".concat(prefix, "--side-nav__submenu ").concat(prefix, "--side-nav__submenu-platform")
  }, platform.name), React.createElement(SideNavItems, null, React.createElement(HeaderSideNavItems, null, sideNav))));
};

var preventOutFocus = function preventOutFocus(target, isSideNavExpanded) {
  if (isSideNavExpanded) {
    target.focus();
  }
};
/**
 * Loops through and renders a list of links for the side nav
 *
 * @param {Array} sections A list of link sections to be rendered
 * @param {string} backButtonText back button text
 * @param {string} autoid autoid predecessor
 * @param {string} navType navigation type
 * @returns {object} JSX object
 */


function renderNavSections(sections, backButtonText, autoid, navType) {
  var sectionItems = [];
  sections.forEach(function (section) {
    section.menuItems.forEach(function (item, j) {
      var dataAutoId = "".concat(autoid, "-list").concat(j);

      if (item.megapanelContent) {
        sectionItems.push(React.createElement(SideNavMenuWithBackFoward, {
          title: item.title,
          titleUrl: item.url,
          backButtonText: backButtonText,
          autoid: dataAutoId,
          navType: navType,
          key: j
        }, renderNavItem(item.megapanelContent.quickLinks.links, dataAutoId), React.createElement("button", {
          className: "".concat(prefix, "--masthead__focus"),
          onFocus: function onFocus(e) {
            preventOutFocus(e.target.parentElement.querySelector('a'), true);
          },
          "aria-hidden": true
        })));
      } else {
        sectionItems.push(React.createElement(SideNavMenuItem, {
          href: item.url,
          "data-autoid": dataAutoId,
          key: item.title
        }, item.title));
      }

      if (j === section.menuItems.length - 1) {
        sectionItems.push(React.createElement("button", {
          className: "".concat(prefix, "--masthead__focus"),
          onFocus: function onFocus(e) {
            preventOutFocus(e.target.parentElement.querySelector('a'), true);
          },
          "aria-hidden": true
        }));
      }
    });
  });
  return sectionItems;
}
/**
 * Loops through and renders a list of links for the side nav
 *
 * @param {Array} items A list of links to be rendered
 * @param {string} autoid autoid predecessor
 * @returns {object} JSX object
 */


function renderNavItem(items, autoid) {
  var navItems = [];
  items.forEach(function (item, i) {
    var dataAutoId = "".concat(autoid, "-item").concat(i);
    navItems.push(React.createElement(SideNavMenuItem, {
      href: item.url,
      "data-autoid": dataAutoId,
      key: item.title
    }, item.title));
  });
  return navItems;
}

MastheadLeftNav.propTypes = {
  /**
   * Back button text
   */
  backButtonText: PropTypes.string,

  /**
   * Object containing left navigation elements.
   */
  navigation: PropTypes.arrayOf(PropTypes.shape({
    hasMenu: PropTypes.bool,
    title: PropTypes.string,
    url: PropTypes.string,
    menuSections: PropTypes.arrayOf(PropTypes.shape({
      menuItems: PropTypes.arrayOf(PropTypes.shape({
        title: PropTypes.string,
        url: PropTypes.string
      }))
    }))
  })),

  /**
   * `true` to make the sidenav expanded.
   */
  isSideNavExpanded: PropTypes.bool,

  /**
   * Platform object with name and url
   */
  platform: PropTypes.shape({
    name: PropTypes.string,
    url: PropTypes.string
  })
};
MastheadLeftNav.defaultProps = {
  backButtonText: 'Back'
};
export default MastheadLeftNav;